import { z } from '@/utils/zod'

import { ScopeDisplaySchema, ScopeNameSchema } from './oauth'

export const ResourceServerIdSchema = z.string().length(21)

export type ResourceServerId = z.infer<typeof ResourceServerIdSchema>

export const ResourceServerScopeSchema = z.object({
    id: z.string().length(21),
    name: ScopeNameSchema,
    display: ScopeDisplaySchema,
})

export const ResourceServerAudienceSchema = z.discriminatedUnion('mode', [
    z.object({
        mode: z.literal('indicator'),
    }),
    z.object({
        mode: z.literal('custom'),
        value: z.string().min(1).max(2048),
    }),
])

export const ResourceServerJwtSigningSchema = z.discriminatedUnion('keySource', [
    z.object({
        keySource: z.literal('tenantSigningKey'),
        alg: z.enum(['RS256']),
        kid: z.string().min(1).max(128).optional(),
    }),
])

export const ResourceServerAccessTokenSchema = z.discriminatedUnion('format', [
    z.object({
        format: z.literal('opaque'),
        ttl: z.int().min(1),
    }),
    z.object({
        format: z.literal('jwt'),
        ttl: z.int().min(1),
        signing: ResourceServerJwtSigningSchema,
    }),
])

// export const ResourceServerSubjectPolicySchema = z.object({
//     emailVerified: z.enum(['inherit', 'forceTrue']).default('inherit'),
// })

export const ResourceServerSchema = z
    .object({
        id: ResourceServerIdSchema,
        enabled: z.boolean(),

        name: z.string().min(1).max(120),
        description: z.string().max(1000).default(''),

        indicator: z.url().max(2048),
        audience: ResourceServerAudienceSchema,

        scopes: z.array(ResourceServerScopeSchema),

        accessToken: ResourceServerAccessTokenSchema,
        // subject: ResourceServerSubjectPolicySchema,
    })
    .superRefine((resource, ctx) => {
        const scopeIds = resource.scopes.map((scope) => scope.id)
        const scopeNames = resource.scopes.map((scope) => scope.name)

        if (new Set(scopeIds).size !== scopeIds.length) {
            ctx.issues.push({
                code: 'custom',
                input: resource.scopes,
                path: ['scopes'],
                message: 'Resource server scopes must be unique by id',
            })
        }

        if (new Set(scopeNames).size !== scopeNames.length) {
            ctx.issues.push({
                code: 'custom',
                input: resource.scopes,
                path: ['scopes'],
                message: 'Resource server scopes must be unique by name',
            })
        }
    })

export type ResourceServer = z.infer<typeof ResourceServerSchema>
export type ResourceServerScope = z.infer<typeof ResourceServerScopeSchema>
